/**
 * Defines symbolic constants representing different age ranges.
 */
export const AgeRange = {
  _20_to_69: Symbol(0),
  _70_to_74: Symbol(1),
  _75_to_79:  Symbol(2),
  _80_to_84: Symbol(3),
  _85_and_up: Symbol(4),
};

/**
 * Returns the appropriate AgeRange constant based on the given age.
 * @param {number} age - The age of the individual.
 * @returns {symbol} A symbol from AgeRange corresponding to the age bracket.
 */
export function getAgeRange(age) {
  if (age < 70) {
    return AgeRange._20_to_69;
  } else if (age < 75) {
    return AgeRange._70_to_74;
  } else if (age < 80) {
    return AgeRange._75_to_79;
  } else if (age < 85) {
    return AgeRange._80_to_84;
  } else {
    return AgeRange._85_and_up;
  }
}

/**
 * Retrieves a normal gait speed value (in cm/s).
 * Currently returns 92.8 cm/s for all age ranges.
 * @param {symbol} ageRange - A symbol from AgeRange.
 * @returns {number} The normal gait speed (in cm/s).
 */
export function getNormGaitSpeed(ageRange) {
  return 92.8;
}

/**
 * Calculates an estimated fall risk percentage given a gait speed in m/s and the person's age.
 * @param {number} gait_speed - The individual's gait speed in meters per second (m/s).
 * @param {number} age - The individual's age in years.
 * @returns {number} The estimated fall risk as a percentage (0.0-100.0).
 */
export function calculateFallRisk(gait_speed, age) {
  const age_range = getAgeRange(age);

  // No fall risk calculation for age < 70
  if (age_range === AgeRange._20_to_69) {
    return 0.0;
  }

  // Retrieve the normal gait speed for the age range (in cm/s)
  const norm_gait_speed = getNormGaitSpeed(age_range);

  // Convert the user's gait speed to cm/s
  const gait_speed_cm_s = gait_speed * 100;

  // Study references: 10 cm/s decrease in gait speed => 1.069x risk of a fall
  const riskNum = 1.069;
  const riskDen = 10;
  const normalRisk = 44.0;

  // Calculate risk ratio and then absolute risk
  const power = (norm_gait_speed - gait_speed_cm_s) / riskDen;
  const riskRatio = Math.pow(riskNum, power);
  let absolute_risk = Math.min(100, riskRatio * normalRisk);

  // Round to 2 decimal places
  absolute_risk = parseFloat((absolute_risk * 100).toFixed(2)) / 100;

  return absolute_risk;
}